import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {
  CommissionProviderFmsBooking,
  CommissionReconciliationAdjustmentRequest,
  CommissionReconciliationFilter,
  CommissionReconciliationResponse,
} from '../models/commission-reconciliation';
import {catchError, Observable, of, switchMap} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CommissionReconciliationService {
  private readonly httpClient = inject(HttpClient);

  public readonly triggerModal = signal(false);

  public readonly PATH = 'dbov2/onyx-data';

  public list(queryParams: CommissionReconciliationFilter) {
    const params = new HttpParams({ fromObject: { ...queryParams } });
    return this.httpClient.get<CommissionReconciliationResponse>(this.PATH, {
      params,
    });
  }

  public uploadFile(file: File) {
    const headers = new HttpHeaders({
      'Content-Type': 'multipart/form-data',
    });
    const form = new FormData();
    form.append('ZipFile', file);
    return this.httpClient.post<void>(this.PATH, form, { headers });
  }

  public downloadPdf(params: Record<string, any>) {
    return this.httpClient
      .get<{ pdfBase64: string }>(this.PATH, { params })
      .pipe(
        switchMap((response) => {
          if (response && response.pdfBase64) {
            const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
            const fileName = `commission-reconciliation_${timestamp}.pdf`;
            this.savePdfFromBase64(response.pdfBase64, fileName);
          } else {
            console.error('Invalid PDF response:', response);
          }
          return of(response);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Error downloading PDF:', error);
          throw new Error(error.error);
        })
      );
  }

  /**
   * Converts a Base64 string to a Blob and triggers download
   * @param base64String The Base64 encoded PDF content
   * @param fileName The name to use for the downloaded file
   */
  private savePdfFromBase64(base64String: string, fileName: string): void {
    try {
      const base64Content = base64String.replace(
        /^data:application\/pdf;base64,/,
        ''
      );

      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName || 'document.pdf';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, 100);
    } catch (error) {
      console.error('Error processing PDF:', error);
    }
  }

  adjustmentCommissionReconciliations(request:CommissionReconciliationAdjustmentRequest): Observable<boolean>{
    return this.httpClient.put<boolean>(`dbov2/commission-reconciliation-adjustment`, 
      { bankId: request.bankId, processCommissionReconciliationRequest: request.processCommissionReconciliationRequest })
      .pipe(catchError((error:HttpErrorResponse)=>{
          throw new Error(error.message || 'Server error')
      }));
  }

  addMapping(payload: CommissionProviderFmsBooking) : Observable<number> {
    return this.httpClient.post<number>(`dbov2/commission-provider-fms-booking`, payload);
  }
}
